// ============================================================================
// Canonical registration identity + session resolver.
//
// ONE place decides, for any registration row:
//   • which canonical class it belongs to        (delegated to classLabels)
//   • which canonical session / registration period it belongs to
//   • which review bucket it lands in when something is missing
//
// No admin surface may infer a period from a display string on its own.
// ============================================================================

import {
  resolveEnrollmentPeriodAndScheduleOption,
  normalizeSessionLabel,
  stripSessionDecoration,
  isArchivedSessionLabel,

  type EnrollmentPeriod,
} from "@/lib/enrollment";

/**
 * Buckets replace the old catch-all "Unspecified" tab.
 *
 * "Legacy Registration" is RETIRED. A genuine registration for a session that
 * has already been delivered is HISTORICAL, not broken — it must never render
 * as an error just because the class's current session list changed.
 */
export type ReviewBucket =
  | "Needs Session Assignment"
  | "Needs Class Assignment"
  | "Needs Resolution"
  | "Historical"
  | "Test Registration";

export const REVIEW_BUCKETS: ReviewBucket[] = [
  "Needs Class Assignment",
  "Needs Session Assignment",
  "Needs Resolution",
  "Historical",
  "Test Registration",
];

/** Buckets that represent real, actionable work on current/future records. */
export const ACTIONABLE_BUCKETS: ReviewBucket[] = [
  "Needs Class Assignment",
  "Needs Session Assignment",
  "Needs Resolution",
];

export const isReviewBucket = (p: string): p is ReviewBucket =>
  (REVIEW_BUCKETS as string[]).includes(p);

/**
 * Operational cutoff. Registrations whose enrollment was delivered before this
 * date are historical. NOTE: this is applied to the resolved SESSION, never to
 * the registration/payment date — a June purchase of an August session is an
 * August registration.
 */
export const OPERATIONAL_CUTOFF = new Date("2026-07-01T00:00:00-07:00");

/** Why a record could not be placed in a real period. */
export type UnresolvedReason =
  | "missing_session_label"
  | "unresolved_class_id"
  | "stale_session_name"
  | "legacy_registration"
  | "test_record"
  | "orphaned_record";

export const REASON_TEXT: Record<UnresolvedReason, string> = {
  missing_session_label: "No session was recorded at checkout.",
  unresolved_class_id: "The stored class ID does not match any known class.",
  stale_session_name: "This session is no longer offered — kept for history.",
  legacy_registration: "Historical record from before sessions were tracked.",
  test_record: "Looks like test data — not a real family registration.",
  orphaned_record: "Could not be matched to a class or a session.",
};

/** Minimal shape every caller can supply. */
export interface RegistrationIdentityInput {
  /** Internal registration/payment UUID — the ONLY id safe for writes. */
  paymentId: string | null | undefined;
  stripeSessionId?: string | null;
  classIdRaw: string | null | undefined;
  classIsUnknown: boolean;
  sessionLabels: string[];
  canonicalSessionLabel?: string | null;
  email?: string | null;
  adultName?: string | null;
  childName?: string | null;
  amountCents?: number | null;
  isTestRecord?: boolean | null;
  /** Registration/payment creation date. Used ONLY to place unresolvable
   *  records on the correct side of the operational cutoff — never to infer
   *  the period of a resolvable session. */
  createdAt?: string | Date | null;
  /** Sessions currently configured on the class ({label, dates}). When
   *  present, a reusable "Session N" label on a current (non-archived,
   *  post-cutoff) registration is placed by THAT session's dates rather
   *  than the legacy summer-month number mapping. */
  currentClassSessions?: { label: string; dates: string }[] | null;
}


export interface ResolvedRegistrationSession {
  /** Real period, or null when the record needs review. */
  period: EnrollmentPeriod | null;
  option: string | null;
  label: string | null;
  bucket: ReviewBucket | null;
  reason: UnresolvedReason | null;
  reasonText: string | null;
  /** Stripe environment for the record. */
  stripeMode: "live" | "test" | "free" | "none";
  probableTest: boolean;
  /** A live, completed Stripe payment must never be hard-removed. */
  liveProtected: boolean;
  /** Session already delivered — belongs in Historical, never an error. */
  historical: boolean;
  /** Dates text of the matched current class session, when resolved by dates. */
  sessionDates?: string | null;
}

/** Month index (0-11) of a resolvable summer period. */
const PERIOD_MONTH_INDEX: Partial<Record<EnrollmentPeriod, number>> = {
  May: 4, June: 5, July: 6, August: 7,
};

function currentMonthIndex(at: Date, tz = "America/Los_Angeles"): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, month: "numeric" })
    .formatToParts(at);
  return Number(parts.find((p) => p.type === "month")?.value ?? "1") - 1;
}

/**
 * A resolved period is historical when its month has already passed.
 * Semesters are never historical here — they carry their own lifecycle.
 */
export function isHistoricalPeriod(
  period: EnrollmentPeriod | null,
  at: Date = new Date(),
): boolean {
  if (!period) return false;
  const idx = PERIOD_MONTH_INDEX[period];
  if (idx == null) return false;
  return idx < currentMonthIndex(at);
}


const TEST_EMAIL_RE = /(@example\.(com|org|net)$|^test[\w.+-]*@|\+test@)/i;
const TEST_NAME_RE = /(^|\s)test(\s|$)/i;

export function stripeModeOf(sessionId: string | null | undefined): ResolvedRegistrationSession["stripeMode"] {
  const s = (sessionId ?? "").trim();
  if (!s) return "none";
  if (s.startsWith("cs_live")) return "live";
  if (s.startsWith("cs_test")) return "test";
  if (s.startsWith("free-")) return "free";
  return "none";
}

/**
 * Probable-test detection.
 * A name containing "test" is NOT enough on its own when the record is tied to
 * a completed live Stripe payment — those stay real until an admin says otherwise.
 */
export function looksLikeTestRegistration(input: RegistrationIdentityInput): boolean {
  if (input.isTestRecord) return true;
  const mode = stripeModeOf(input.stripeSessionId);
  const email = (input.email ?? "").trim().toLowerCase();
  const nameHit =
    TEST_NAME_RE.test(input.adultName ?? "") || TEST_NAME_RE.test(input.childName ?? "");
  const emailHit = !!email && TEST_EMAIL_RE.test(email);

  if (mode === "test") return true;
  if (emailHit) return true;
  // Name-only signal: only when it is not a real live payment.
  if (nameHit && mode !== "live") return true;
  return false;
}

/**
 * The labels a registration actually purchased.
 * RAW `selected_sessions` win over `canonical_session_label`: the canonical
 * value is trigger-generated and may carry decoration derived from the
 * registration date. Canonical is only a fallback when nothing was stored.
 */
export function purchasedLabels(input: RegistrationIdentityInput): string[] {
  const raw = (input.sessionLabels ?? []).filter(Boolean);
  const source = raw.length
    ? raw
    : input.canonicalSessionLabel
      ? [input.canonicalSessionLabel]
      : [];
  const out: string[] = [];
  for (const entry of source) {
    const undecorated = stripSessionDecoration(entry) ?? String(entry).trim();
    // A legacy multi-pick saved as one string ("Session 3, Session 4") is
    // split into its components ONLY when every part is a bare "Session N",
    // so labels containing commas inside dates are never broken apart.
    const plusParts = undecorated.split(/\s\+\s/);
    const parts = plusParts.flatMap((pp) => {
      const commaParts = pp.split(/\s*,\s*/).map((x) => x.trim()).filter(Boolean);
      return commaParts.length > 1 && commaParts.every((x) => /^session\s*\d+$/i.test(x)) ? commaParts : [pp];
    });
    for (const part of parts) {
      const norm = normalizeSessionLabel(part);
      if (norm && !/single\s*session/i.test(norm) && !out.includes(norm)) out.push(norm);
    }
  }
  return out;
}

function createdBeforeCutoff(createdAt: RegistrationIdentityInput["createdAt"]): boolean {
  if (!createdAt) return false;
  const d = createdAt instanceof Date ? createdAt : new Date(createdAt);
  return !Number.isNaN(d.getTime()) && d < OPERATIONAL_CUTOFF;
}

export function resolveRegistrationSession(
  input: RegistrationIdentityInput,
): ResolvedRegistrationSession {
  const stripeMode = stripeModeOf(input.stripeSessionId);
  const probableTest = looksLikeTestRegistration(input);
  const liveProtected = stripeMode === "live" && (input.amountCents ?? 0) > 0;

  const base = { stripeMode, probableTest, liveProtected };
  const labels = purchasedLabels(input);
  // Unresolvable records created before the operational cutoff are history
  // needing later resolution — they must not clutter current operations.
  const preCutoff = createdBeforeCutoff(input.createdAt);

  // 1. Class identity must resolve first — a registration on an unknown class
  //    cannot be trusted to a period.
  if (input.classIsUnknown) {
    return {
      ...base,
      period: null,
      option: null,
      label: labels[0] ?? null,
      bucket: probableTest ? "Test Registration" : preCutoff ? "Historical" : "Needs Class Assignment",
      reason: probableTest ? "test_record" : "unresolved_class_id",
      reasonText: probableTest
        ? REASON_TEXT.test_record
        : `${REASON_TEXT.unresolved_class_id} Stored ID: ${input.classIdRaw ?? "—"}`,
      historical: !probableTest && preCutoff,
    };
  }

  // 2. No usable session label at all.
  if (!labels.length) {
    return {
      ...base,
      period: null,
      option: null,
      label: null,
      bucket: probableTest ? "Test Registration" : preCutoff ? "Historical" : "Needs Session Assignment",
      reason: probableTest ? "test_record" : "missing_session_label",
      reasonText: probableTest ? REASON_TEXT.test_record : REASON_TEXT.missing_session_label,
      historical: !probableTest && preCutoff,
    };
  }

  // 3a. Reusable "Session N" on a CURRENT registration: place it by the
  //     class's configured session dates, never by the number alone.
  //     Archived-decorated or pre-cutoff records keep legacy behavior.
  const sourceArchived = [...(input.sessionLabels ?? []), input.canonicalSessionLabel ?? ""]
    .some((l) => isArchivedSessionLabel(l));
  if (
    /^Session\s+\d+$/i.test(labels[0]) &&
    input.currentClassSessions &&
    !preCutoff &&
    !sourceArchived
  ) {
    const want = labels[0].toLowerCase();
    const match = input.currentClassSessions.find(
      (s) => (normalizeSessionLabel(s.label) ?? "").toLowerCase() === want,
    );
    const datePeriod = match
      ? resolveEnrollmentPeriodAndScheduleOption(match.dates).period
      : "Unspecified";
    if (datePeriod !== "Unspecified") {
      return {
        ...base,
        period: datePeriod,
        option: null,
        label: labels[0],
        bucket: null,
        reason: null,
        reasonText: null,
        historical: isHistoricalPeriod(datePeriod),
        sessionDates: match?.dates ?? null,
      };
    }
    return {
      ...base,
      period: null,
      option: null,
      label: labels[0],
      bucket: probableTest ? "Test Registration" : "Needs Resolution",
      reason: probableTest ? "test_record" : "missing_session_label",
      reasonText: probableTest
        ? REASON_TEXT.test_record
        : `“${labels[0]}” doesn't match a current session's dates for this class — confirm which offering this is.`,
      historical: false,
    };
  }

  // 3. Resolve through the shared axis resolver.
  const resolved = resolveEnrollmentPeriodAndScheduleOption(labels[0]);
  if (resolved.period === "Unspecified") {
    const missing = /^unspecified$/i.test(labels[0]);
    // A stale session name is NOT an error — it is history, unless the record
    // is genuinely current and never had a session recorded.
    const bucket: ReviewBucket = probableTest
      ? "Test Registration"
      : missing && !preCutoff
        ? "Needs Session Assignment"
        : "Historical";
    const reason: UnresolvedReason = probableTest
      ? "test_record"
      : missing
        ? "missing_session_label"
        : "stale_session_name";
    return {
      ...base,
      period: null,
      option: null,
      label: labels[0],
      bucket,
      reason,
      reasonText:
        reason === "stale_session_name"
          ? `Historical session “${labels[0]}” — no longer offered.`
          : REASON_TEXT[reason],
      historical: bucket === "Historical",
    };
  }

  return {
    ...base,
    period: resolved.period,
    option: resolved.option,
    label: labels[0],
    bucket: null,
    reason: null,
    reasonText: null,
    historical: isHistoricalPeriod(resolved.period),
  };
}

/** Every distinct period a registration belongs to (multi-session rows). */
export function resolveAllPeriods(
  input: RegistrationIdentityInput,
): ResolvedRegistrationSession[] {
  const labels = purchasedLabels(input);
  if (labels.length <= 1) return [resolveRegistrationSession(input)];

  const canonicalParts = String(input.canonicalSessionLabel ?? "")
    .split(/\s\+\s/)
    .map((x) => x.trim())
    .filter(Boolean);
  const out: ResolvedRegistrationSession[] = [];
  const seen = new Set<string>();
  for (const label of labels) {
    // Preserve explicit archived context for THIS component: a canonical
    // summary like "Fall Semester + Session 4 · Jul 2026 (archived)" marks
    // only its Session 4 component as archived, never the Fall component.
    const archivedPart = canonicalParts.find(
      (part) =>
        isArchivedSessionLabel(part) &&
        (normalizeSessionLabel(stripSessionDecoration(part)) ?? "").toLowerCase() === label.toLowerCase(),
    );
    const r = resolveRegistrationSession({
      ...input,
      sessionLabels: [archivedPart ?? label],
      canonicalSessionLabel: archivedPart ?? null,
    });
    const key = `${r.period ?? r.bucket}|${r.option ?? "—"}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }

  return out.length ? out : [resolveRegistrationSession(input)];
}
